import { API_BASE_URL } from '../config/apiBase';

/** Read csrftoken from the current document cookies (SPA origin). */
export function readCsrfCookie() {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  if (!match) {
    return null;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Django CSRF secrets are typically ~64 characters.
 * Reject empty values and common JS garbage like "undefined".
 */
export function isPlausibleCsrfToken(token) {
  if (typeof token !== 'string') {
    return false;
  }
  const trimmed = token.trim();
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
    return false;
  }
  return trimmed.length >= 32 && trimmed.length <= 128;
}

/**
 * Obtain a CSRF token for mutating API calls.
 * Prefer a fresh server-issued token when forced or when the cookie looks invalid
 * (stale/truncated cookies cause Django "incorrect length" 403s).
 */
export async function ensureCsrfToken({ forceRefresh = false } = {}) {
  if (!forceRefresh) {
    const existing = readCsrfCookie();
    if (isPlausibleCsrfToken(existing)) {
      return existing.trim();
    }
  }

  const res = await fetch(`${API_BASE_URL}/csrf/`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (res.ok) {
    try {
      const data = await res.json();
      if (isPlausibleCsrfToken(data?.csrfToken)) {
        return String(data.csrfToken).trim();
      }
    } catch {
      // Fall through to cookie / session bootstrap.
    }
  }

  // session/ also sets the CSRF cookie via @ensure_csrf_cookie.
  await fetch(`${API_BASE_URL}/session/`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  const fromCookie = readCsrfCookie();
  if (isPlausibleCsrfToken(fromCookie)) {
    return fromCookie.trim();
  }

  throw new Error('Unable to obtain a valid CSRF token');
}
